import { createReducer, on } from '@ngrx/store';
import { EditCampaignSuccess } from '../actions/campaign-search.actions';
import {
  AdditionalRecipientsPanelChanged,
  AdvancedFilterConfigurationPanelChanged,
  AdvancedHelpTextChanged,
  CampaignAdhocListChanged,
  CampaignAdhocListNameChanged,
  CampaignAdhocListNameCleared,
  CampaignAdhocListsSuccess,
  CardTypesSuccess,
  ClearEstimationResult,
  ControlGroupPercentageChanged,
  EstimationResultFailure,
  EstimationResultSuccess,
  EstimationRunFailure,
  EstimationRunInit,
  EstimationRunSuccess,
  CancelEstimationPolling,
  FilterSelected,
  FilterUpdated,
  getAvailableLanguagesSuccess,
  getCommunicationChannelsSuccess,
  GetCouponSetAttributesSuccess,
  GetCouponSetsFastSuccess,
  GetCouponSetsSuccess,
  getMemberClubsSuccess,
  getMemberSourcesSuccess,
  GetMemberStatusesSuccess,
  GetOptOutFlagsSuccess,
  getPointSourcesSuccess,
  getRFMSuccess,
  getTagsSuccess,
  getTiersSuccess,
  getCountriesSuccess,
  getRegionsSuccess,
  getMTVSuccess,
  getPersonSourcesSuccess,
  getReportedEmailCampaignsSuccess,
  getReportedInAppCampaignsSuccess,
  getReportedPushCampaignsSuccess,
  getProductCategoryLevelsSuccess,
  getProductCategoriesSuccess,
  getGenericActivityTypesSuccess,
  HelpTextChanged,
  MemberCategoriesSuccess,
  OpenFilterConfirmationModal,
  RecipientLimitChanged,
  RemoveFilter,
  SearchCampaignsFailure,
  SearchCampaignsSuccess,
  SelectionsByTargetSuccess,
  ShowFilterInfo,
  SiriusAccountsSuccess,
  SocialMediaProvidersSuccess,
  StoresSuccess,
  TargetGroupFiltersPanelChanged,
} from '../actions/wizard-who.actions';
import { ResetWizard } from '../actions/wizard.actions';
import { CampaignSelectionTargetChanged } from '../actions/wizard-setup.actions';
import { WizardWhoState } from '../models/wizard.model';
import {
  LOYALTY_TEMPLATES_DATA,
  PERSON_TEMPLATES_DATA,
  SHARED_TEMPLATES_DATA,
} from '../components/wizard/steps/who/helpers/filter-template-data';

const initialState: WizardWhoState = {
  helpTextStatus: false,
  advancedHelpTextStatus: false,
  targetGroupFiltersPanelExpanded: true,
  advancedFilterConfigurationPanelExpanded: false,
  additionalRecipientsPanelExpanded: false,
  filterModalOpened: false,
  selections: [],
  memberCategories: [],
  cardTypes: [],
  siriusAccounts: [],
  socialMediaProviders: [],
  stores: [],
  campaignAdhocLists: [],
  selectedCampaignAdhocList: null,
  selectedCampaignAdhocListName: '',
  adhocListPreview: null,
  selectedFilters: [],
  showFilterInfo: false,
  controlGroupPercentage: 0,
  recipientLimit: null,
  disableEstimationResultButton: true,
  filterConfirmationModalSource: '',
  campaignEstimationIdentifier: null,
  estimationResult: null,
  estimationPolling: false,
  searchedCampaigns: [],
  optOutFlags: {
    EMAIL: [],
    SMS: [],
    VIBER: [],
  },
  memberStatuses: [],
  couponSets: [],
  attributeCodes: [],
  pointSources: [],
  memberSources: [],
  availableLanguages: [],
  memberClubs: [],
  tags: [],
  communicationChannels: [],
  tiers: [],
  rfm: [],
  countries: [],
  regions: [],
  mtv: [],
  personSources: [],
  reportedEmailCampaigns: [],
  reportedInAppCampaigns: [],
  reportedPushCampaigns: [],
  genericActivityTypes: null,
  productCategoryLevels: [],
  productCategories: {},
};

/**
 * Backend reference/lookup data. It is not campaign specific, so it survives a wizard reset.
 * It is only fetched by `ExecuteInitialCalls`, which is dispatched when the user steps forward
 * through the wizard - so wiping it on reset would leave the filter dropdowns empty whenever a
 * campaign is opened straight into the "Who" step for edit.
 */
const preserveLookups = (state: WizardWhoState) => ({
  selections: state.selections,
  memberCategories: state.memberCategories,
  cardTypes: state.cardTypes,
  siriusAccounts: state.siriusAccounts,
  socialMediaProviders: state.socialMediaProviders,
  stores: state.stores,
  campaignAdhocLists: state.campaignAdhocLists,
  optOutFlags: state.optOutFlags,
  memberStatuses: state.memberStatuses,
  couponSets: state.couponSets,
  attributeCodes: state.attributeCodes,
  pointSources: state.pointSources,
  memberSources: state.memberSources,
  availableLanguages: state.availableLanguages,
  memberClubs: state.memberClubs,
  tags: state.tags,
  communicationChannels: state.communicationChannels,
  tiers: state.tiers,
  rfm: state.rfm,
  countries: state.countries,
  regions: state.regions,
  mtv: state.mtv,
  personSources: state.personSources,
  reportedEmailCampaigns: state.reportedEmailCampaigns,
  reportedInAppCampaigns: state.reportedInAppCampaigns,
  reportedPushCampaigns: state.reportedPushCampaigns,
  genericActivityTypes: state.genericActivityTypes,
  productCategoryLevels: state.productCategoryLevels,
  productCategories: state.productCategories,
});

const campaignScopedReset = {
  helpTextStatus: initialState.helpTextStatus,
  advancedHelpTextStatus: initialState.advancedHelpTextStatus,
  targetGroupFiltersPanelExpanded: initialState.targetGroupFiltersPanelExpanded,
  advancedFilterConfigurationPanelExpanded: initialState.advancedFilterConfigurationPanelExpanded,
  additionalRecipientsPanelExpanded: initialState.additionalRecipientsPanelExpanded,
  filterModalOpened: initialState.filterModalOpened,
  showFilterInfo: initialState.showFilterInfo,
  filterConfirmationModalSource: initialState.filterConfirmationModalSource,
  adhocListPreview: initialState.adhocListPreview,
  searchedCampaigns: initialState.searchedCampaigns,
  campaignEstimationIdentifier: initialState.campaignEstimationIdentifier,
  estimationResult: initialState.estimationResult,
  estimationPolling: initialState.estimationPolling,
};

export const WizardWhoReducer = createReducer(
  initialState,
  on(EditCampaignSuccess, (state, { campaign }) => {
    const listId = campaign.data.additionalRecipientsListId;
    const matchedList = state.campaignAdhocLists.find((l) => l.campaignAdHocListId === listId);
    const rawFilters = campaign.data.campaignWho || [];
    const ALL_TEMPLATES = [...LOYALTY_TEMPLATES_DATA, ...PERSON_TEMPLATES_DATA, ...SHARED_TEMPLATES_DATA];

    const enrichedFilters = rawFilters.map((rawBackendFilter: any, index: number) => {
      const backendFilter = { ...rawBackendFilter };

      const templateData = ALL_TEMPLATES.find((t) => t.selectionType === backendFilter.selectionType);
      if (!templateData) return backendFilter;

      let generatedDisplayValue = templateData.defaults['displayFormat'] || templateData.defaults['displayValue'] || '';
      if (generatedDisplayValue && generatedDisplayValue.includes('{')) {
        Object.keys(backendFilter).forEach((key) => {
          const value = backendFilter[key] !== null && backendFilter[key] !== undefined ? backendFilter[key] : '';
          generatedDisplayValue = generatedDisplayValue.replace(`{${key}}`, value.toString());
        });
      }

      switch (backendFilter.selectionType) {
        case 'PERSON_BIRTHDATE':
        case 'MEMBER_BIRTHDATE':
        case 'MEMBER_NAMEDAY':
          const days = backendFilter.daysAfter || 0;
          const birthdayLabel =
            backendFilter.selectionType === 'MEMBER_BIRTHDATE'
              ? "member's birthday"
              : backendFilter.selectionType === 'MEMBER_NAMEDAY'
                ? "person's nameday"
                : "person's birthday";
          if (days < 0) generatedDisplayValue = `${Math.abs(days)} days before the ${birthdayLabel}`;
          else if (days > 0) generatedDisplayValue = `${days} days after the ${birthdayLabel}`;
          else generatedDisplayValue = `On the ${birthdayLabel}`;
          break;

        case 'PERSON_GENDER_SELECTION':
        case 'MEMBER_GENDER_SELECTION':
          const genders = backendFilter.genderList || [];
          if (genders.length > 0) {
            generatedDisplayValue = genders
              .map((g: string) => g.charAt(0).toUpperCase() + g.slice(1).toLowerCase())
              .join(' , ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Choose a gender type';
          }
          break;

        case 'PERSON_ATTRIBUTES':
        case 'MEMBER_ATTRIBUTES':
        case 'MEMBER_PERSON_ATTRIBUTES':
        case 'MEMBER_INVOICE_ATTRIBUTES':
          const validFilters = (backendFilter.attributeFilters || []).filter(
            (f: any) => f.attributeType && f.attributeName,
          );

          if (validFilters.length > 0) {
            generatedDisplayValue = validFilters
              .map((f: any) => {
                let str = `${f.attributeName}: `;
                if (f.attributeType === 'STRING' || f.attributeType === 'BOOLEAN') str += f.attributeValue;
                if (f.attributeType === 'INTEGER') str += `${f.operator} ${f.attributeValue}`;
                if (f.attributeType === 'DATE') {
                  if (f.durationType === 'date') {
                    const dateStr = f.date ? new Date(f.date).toLocaleDateString('en-GB').replace(/\//g, '-') : '';
                    str += `${f.operator} ${dateStr}`;
                  } else {
                    str += `${f.operator} ${f.days || 0}d ${f.months || 0}m`;
                  }
                }
                return str;
              })
              .join(' , ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Choose attributes';
          }

          backendFilter.attributeFilters = [
            ...(backendFilter.attributeFilters || []),
            { attributeType: null, attributeName: null, attributeValue: null },
          ];
          break;

        case 'PERSON_INVOICE':
        case 'MEMBER_INVOICE':
          const invFields = backendFilter.invoiceFields || [];
          const hasInvDate = invFields.some((f: any) => f.fieldName === 'INVOICE_DATE');
          const hasInvNum = invFields.some((f: any) => f.fieldName === 'INVOICE_NUMBER');
          const parts = [];

          if (hasInvDate && backendFilter.comperator) {
            const opLabel = backendFilter.comperator.replace(/_/g, ' ').toLowerCase();
            if (backendFilter.durationType === 'date' && backendFilter.invoiceDate) {
              const dStr = new Date(backendFilter.invoiceDate).toLocaleDateString('en-GB').replace(/\//g, '-');
              parts.push(`${opLabel} ${dStr}`);
            } else if (backendFilter.durationType === 'daysMonths') {
              parts.push(`${opLabel} ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`);
            } else {
              parts.push(opLabel);
            }
          }

          if (hasInvNum) {
            const numField = invFields.find((f: any) => f.fieldName === 'INVOICE_NUMBER');
            if (numField?.value) parts.push(`Number: ${numField.value}`);
          }

          generatedDisplayValue =
            parts.length > 0
              ? parts.join(' | ')
              : templateData.defaults['headerPlaceholder'] || 'Choose invoice fields';

          if (invFields.length < 2) {
            backendFilter.invoiceFields = [...invFields, { fieldName: null, value: null }];
          }
          break;

        case 'PERSON_UI_PRODUCT':
          if (backendFilter.comperator) {
            const opLabel = backendFilter.comperator.replace(/_/g, ' ').toLowerCase();
            if (backendFilter.durationType === 'date' && backendFilter.relationStartDate) {
              const dStr = new Date(backendFilter.relationStartDate).toLocaleDateString('en-GB').replace(/\//g, '-');
              generatedDisplayValue = `${opLabel} ${dStr}`;
            } else if (backendFilter.durationType === 'daysMonths') {
              generatedDisplayValue = `${opLabel} ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
            } else {
              generatedDisplayValue = opLabel;
            }
          }
          break;

        case 'MEMBER_EXCLUDE':
        case 'MEMBER_MULTIPLE_CAMPAIGN_EXCLUDE':
        case 'PERSON_IN_CAMPAIGNS':
          if (backendFilter.year) generatedDisplayValue = 'This year';
          else if (backendFilter.month) generatedDisplayValue = 'This month';
          else {
            const excludeParts = [];
            if (backendFilter.days) excludeParts.push(`in the last ${Math.abs(backendFilter.days)} days`);
            if (backendFilter.months) excludeParts.push(`in the last ${Math.abs(backendFilter.months)} months`);
            generatedDisplayValue =
              excludeParts.length > 0
                ? excludeParts.join(' and ')
                : templateData.defaults['headerPlaceholder'] || 'Configuring...';
          }
          break;

        case 'MEMBER_INVOICE_INACTIVE':
        case 'PERSON_INVOICE_INACTIVE':
          if (backendFilter.days) generatedDisplayValue = `for ${Math.abs(backendFilter.days)} days`;
          else if (backendFilter.months) generatedDisplayValue = `for ${Math.abs(backendFilter.months)} months`;
          else generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Set days or months';
          break;

        case 'MEMBER_CATEGORY':
          const matchedCategory = state.memberCategories.find(
            (c: any) => c.tableMemberCategoryId === backendFilter.tableMemberCategoryId,
          );
          generatedDisplayValue = matchedCategory
            ? matchedCategory.memberCategory
            : templateData.defaults['headerPlaceholder'] || 'Select a category';
          break;

        case 'MEMBER_STATUS':
          const statusIds: number[] = backendFilter.memberStatusIdList || [];
          if (statusIds.length > 0) {
            generatedDisplayValue = statusIds.map((id: number) => `Status ${id}`).join(' , ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select member statuses';
          }
          break;

        case 'MEMBER_REGISTRATION_STORE':
        case 'PERSON_REGISTRATION_STORE':
          if (backendFilter.registrationStoreId) {
            const matchedStore = state.stores.find((s: any) => s.id === backendFilter.registrationStoreId);
            generatedDisplayValue = matchedStore
              ? matchedStore.description
              : `Store ${backendFilter.registrationStoreId}`;
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select a store';
          }
          break;

        case 'MEMBER_COUPON':
          const couponParts: string[] = [];
          if (backendFilter.couponActiveFunctionality) {
            if (backendFilter.validFrom || backendFilter.validUntil) {
              backendFilter.givenDuration = 'date';
              const from = backendFilter.validFrom ? new Date(backendFilter.validFrom).toLocaleDateString('en-GB') : '';
              const until = backendFilter.validUntil
                ? new Date(backendFilter.validUntil).toLocaleDateString('en-GB')
                : '';
              couponParts.push(`Given - Valid from: ${from} - Valid until: ${until}`);
            } else {
              backendFilter.givenDuration = 'days';
              couponParts.push(
                `Given - valid ${Math.abs(backendFilter.couponActiveLookBackDays || 0)} day(s) before, ${backendFilter.couponActiveLookFrontDays || 0} day(s) after`,
              );
            }
          }
          if (backendFilter.couponUsedFunctionality) {
            if (backendFilter.usedFrom || backendFilter.usedUntil) {
              backendFilter.usedDuration = 'date';
              const from = backendFilter.usedFrom ? new Date(backendFilter.usedFrom).toLocaleDateString('en-GB') : '';
              const until = backendFilter.usedUntil
                ? new Date(backendFilter.usedUntil).toLocaleDateString('en-GB')
                : '';
              couponParts.push(`Used - Valid from: ${from} - Valid until: ${until}`);
            } else {
              backendFilter.usedDuration = 'days';
              couponParts.push(
                `Used - valid ${Math.abs(backendFilter.couponUsedLookBackDays || 0)} day(s) before, ${backendFilter.couponUsedLookFrontDays || 0} day(s) after`,
              );
            }
          }
          generatedDisplayValue =
            couponParts.length > 0
              ? couponParts.join(' | ')
              : templateData.defaults['headerPlaceholder'] || 'Configure coupon filter';
          break;

        case 'COUPON_EXPIRATION':
          if (!backendFilter.durationType) {
            backendFilter.durationType = backendFilter.expirationDate
              ? 'date'
              : backendFilter.daysToExpiration
                ? 'days'
                : '';
          }
          if (backendFilter.durationType === 'date' && backendFilter.expirationDate) {
            generatedDisplayValue = `Expiration date : ${new Date(backendFilter.expirationDate).toLocaleDateString('en-GB').replace(/\//g, '-')}`;
          } else if (backendFilter.durationType === 'days' && backendFilter.daysToExpiration) {
            generatedDisplayValue = `Expiration days : ${backendFilter.daysToExpiration}`;
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Configure expiration';
          }
          break;

        case 'MEMBERS_LOYALTY_POINT_LOGS':
          if (!backendFilter.durationType) {
            backendFilter.durationType = backendFilter.loyaltyPointLogDate ? 'date' : 'daysMonths';
          }
          const plOpLabel = (backendFilter.comperator || '').replace(/_/g, ' ').toLowerCase();
          if (backendFilter.durationType === 'date' && backendFilter.loyaltyPointLogDate) {
            generatedDisplayValue = `${plOpLabel} ${new Date(backendFilter.loyaltyPointLogDate).toLocaleDateString('en-GB').replace(/\//g, '/')}`;
          } else {
            generatedDisplayValue = `${plOpLabel} ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
          }
          break;

        case 'MEMBER_MEMBER_SOURCE':
          const sourceIds: number[] = backendFilter.memberSourceIdList || [];
          if (sourceIds.length > 0) {
            generatedDisplayValue = sourceIds.map((id: number) => `Source ${id}`).join(', ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select member sources';
          }
          break;

        case 'PERSON_PERSON_SOURCE':
        case 'MEMBER_PERSON_SOURCE':
          const personSourceIds: number[] = backendFilter.personSourceIdList || [];
          if (personSourceIds.length > 0) {
            generatedDisplayValue = personSourceIds.map((id: number) => `Source ${id}`).join(', ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select person sources';
          }
          break;

        case 'MEMBER_PREFERRED_LANGUAGE':
        case 'PERSON_PREFERRED_LANGUAGE':
          const langIds: number[] = backendFilter.languageIds || [];
          if (langIds.length > 0) {
            generatedDisplayValue = langIds.map((id: number) => `Language ${id}`).join(', ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select languages';
          }
          break;

        case 'MEMBER_MOBILE_LOGIN':
          if (!backendFilter.durationType && backendFilter.date) {
            backendFilter.durationType = 'DATE';
          } else if (!backendFilter.durationType) {
            backendFilter.durationType = 'DAYS';
          }
          const mlOpLabel = (backendFilter.comparator || '').replace(/_/g, ' ').toLowerCase();
          if (backendFilter.durationType === 'DATE' && backendFilter.date) {
            generatedDisplayValue = `${mlOpLabel} ${new Date(backendFilter.date).toLocaleDateString('en-GB')}`;
          } else {
            generatedDisplayValue = `${mlOpLabel} ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
          }
          break;

        case 'MEMBER_CLUBS':
          const clubIds: number[] = backendFilter.clubIds || [];
          if (clubIds.length > 0) {
            generatedDisplayValue = clubIds.map((id: number) => `Club ${id}`).join(', ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select clubs';
          }
          break;

        case 'MEMBER_INVOICE_INSTORE':
        case 'PERSON_INVOICE_INSTORE':
          if (backendFilter.useDateRange) {
            const from = backendFilter.invoiceDateFrom
              ? new Date(backendFilter.invoiceDateFrom).toLocaleDateString('en-GB')
              : '';
            const until = backendFilter.invoiceDateUntil
              ? new Date(backendFilter.invoiceDateUntil).toLocaleDateString('en-GB')
              : '';
            generatedDisplayValue =
              from || until
                ? `${from} - ${until}`
                : templateData.defaults['headerPlaceholder'] || 'Configure invoice instore';
          } else {
            const isOpLabel = (backendFilter.comperator || '').replace(/_/g, ' ').toLowerCase();
            generatedDisplayValue = `${isOpLabel} ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
          }
          break;

        case 'MEMBER_INVOICE_INREGION':
          if (backendFilter.useDateRange) {
            const from = backendFilter.invoiceDateFrom
              ? new Date(backendFilter.invoiceDateFrom).toLocaleDateString('en-GB')
              : '';
            const until = backendFilter.invoiceDateUntil
              ? new Date(backendFilter.invoiceDateUntil).toLocaleDateString('en-GB')
              : '';
            generatedDisplayValue =
              from || until
                ? `Date from ${from} Date until ${until}`
                : templateData.defaults['headerPlaceholder'] || 'Configure inregion invoice';
          } else {
            const irOpLabel = (backendFilter.comperator || '').replace(/_/g, ' ').toLowerCase();
            generatedDisplayValue = `${irOpLabel} ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
          }
          break;

        case 'MEMBER_IN_TAG':
        case 'PERSON_IN_TAG':
          generatedDisplayValue =
            backendFilter.tagDescription || templateData.defaults['headerPlaceholder'] || 'Choose a tag';
          break;

        case 'MEMBER_IN_TIER':
          const tierCodes: string[] = backendFilter.tierList || [];
          if (tierCodes.length > 0) {
            generatedDisplayValue = tierCodes.join(', ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select tiers';
          }
          break;

        case 'MEMBER_ASSIGNED_TAG':
        case 'PERSON_ASSIGNED_TAG':
          generatedDisplayValue =
            backendFilter.tagDescription || templateData.defaults['headerPlaceholder'] || 'Choose a tag';
          break;

        case 'MEMBER_POINTLOGS_THRESHOLD':
          if (backendFilter.useDateRange === null || backendFilter.useDateRange === undefined) {
            backendFilter.useDateRange = !!(backendFilter.pointsDateFrom || backendFilter.pointsDateUntil);
          }
          const ptOpLabel = (backendFilter.pointsComperator || '').replace(/_/g, ' ').toLowerCase();
          const ptThreshold = backendFilter.pointsThreshold || 0;
          if (backendFilter.useDateRange) {
            const ptFrom = backendFilter.pointsDateFrom
              ? new Date(backendFilter.pointsDateFrom).toLocaleDateString('en-GB')
              : '-';
            const ptUntil = backendFilter.pointsDateUntil
              ? new Date(backendFilter.pointsDateUntil).toLocaleDateString('en-GB')
              : '-';
            generatedDisplayValue = `${ptOpLabel} ${ptThreshold} points from ${ptFrom} to ${ptUntil}`;
          } else {
            generatedDisplayValue = `${ptOpLabel} ${ptThreshold} points in: ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
          }
          break;

        case 'MEMBER_RFM_ANALYSIS':
          const rfmIds: string[] = backendFilter.tableRFMIds || [];
          if (rfmIds.length > 0) {
            generatedDisplayValue = rfmIds.join(', ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select RFM segments';
          }
          break;

        case 'MEMBER_STORE_VISIT_INACTIVE':
          if (backendFilter.days) generatedDisplayValue = `for ${Math.abs(backendFilter.days)} days`;
          else if (backendFilter.months) generatedDisplayValue = `for ${Math.abs(backendFilter.months)} months`;
          else generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Set days or months';
          break;

        case 'MEMBER_PLASTIC_LOYALTYCARD':
          if (backendFilter.fromCardBatchDay || backendFilter.untilCardBatchDay) {
            const plFrom = backendFilter.fromCardBatchDay
              ? new Date(backendFilter.fromCardBatchDay).toLocaleDateString('en-GB')
              : '-';
            const plUntil = backendFilter.untilCardBatchDay
              ? new Date(backendFilter.untilCardBatchDay).toLocaleDateString('en-GB')
              : '-';
            generatedDisplayValue = `from ${plFrom} - until ${plUntil}`;
          } else {
            generatedDisplayValue = `Card batch creation day from ${backendFilter.fromDays || 0} Card batch creation months from ${backendFilter.fromMonths || 0} - Card batch creation day until ${backendFilter.untilDays || 0} Card batch creation months until ${backendFilter.untilMonths || 0}`;
          }
          break;

        case 'MEMBER_LOYALTYCARD_TYPE':
          generatedDisplayValue =
            backendFilter.cardType || templateData.defaults['headerPlaceholder'] || 'Select card type';
          break;

        case 'MEMBER_COUNTRY_POSTCODE':
          const pcCountryId = backendFilter.countryId;
          const pcPostcodes = backendFilter.postCodeList || [];
          if (pcCountryId) {
            const pcCountry = state.countries?.find((c: any) => c.countryId === pcCountryId);
            generatedDisplayValue = pcCountry ? pcCountry.country : `Country ${pcCountryId}`;
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select country and postcodes';
          }
          break;

        case 'MEMBER_COUNTRY_REGION':
        case 'PERSON_COUNTRY_REGION':
          const crRegionIds: number[] = backendFilter.regionIds || [];
          if (crRegionIds.length > 0) {
            generatedDisplayValue = `${crRegionIds.length} region(s) selected`;
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select country and regions';
          }
          break;

        case 'MEMBER_PREFERRED_STORE':
        case 'PERSON_PREFERRED_STORE':
          const prefStoreIds: number[] = backendFilter.storeIds || [];
          if (prefStoreIds.length > 0) {
            generatedDisplayValue = prefStoreIds
              .map((id: number) => {
                const matched = state.stores.find((s: any) => s.id === id);
                return matched ? matched.description : `Store ${id}`;
              })
              .join(', ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select preferred stores';
          }
          break;

        case 'MEMBER_MAX_CAMPAIGN':
        case 'PERSON_MAX_CAMPAIGN':
          const mcParts: string[] = [];
          const mcOpLabel = (backendFilter.comparator || '').replace(/_/g, ' ').toLowerCase();
          const mcCount = backendFilter.campaignExecutionCount || 0;
          const mcChannels = (backendFilter.communicationChannels || []).join(', ');
          mcParts.push(`${mcOpLabel} ${mcCount}`);
          if (mcChannels) mcParts.push(mcChannels);
          if (backendFilter.currentDate) {
            mcParts.push('Current day');
          } else if (backendFilter.dateFrom || backendFilter.dateUntil) {
            const from = backendFilter.dateFrom
              ? new Date(backendFilter.dateFrom).toLocaleDateString('en-GB').replace(/\//g, '-')
              : '';
            const until = backendFilter.dateUntil
              ? new Date(backendFilter.dateUntil).toLocaleDateString('en-GB').replace(/\//g, '-')
              : '';
            mcParts.push(`Campaigns From ${from} until ${until}`);
          }
          generatedDisplayValue = mcParts.join(', ');
          break;

        case 'MEMBER_CLUB_EXPIRATION':
          const ceClubIds: number[] = backendFilter.clubIds || [];
          if (ceClubIds.length > 0) {
            generatedDisplayValue = ceClubIds.map((id: number) => `Club ${id}`).join(', ');
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select clubs';
          }
          break;

        case 'MEMBER_ACTIVE_COUPON':
          const acIds: number[] = backendFilter.couponSetIdList || [];
          generatedDisplayValue =
            acIds.length > 0
              ? `${acIds.length} coupon set(s) selected`
              : templateData.defaults['headerPlaceholder'] || 'Select coupon sets';
          break;

        case 'MEMBER_TIER_EVALUATION_DATE':
          const tedFrom = backendFilter.from || { days: null, months: null, date: null };
          const tedTo = backendFilter.to || { days: null, months: null, date: null };
          if (tedFrom.date || tedTo.date) {
            const tedFromStr = tedFrom.date ? new Date(tedFrom.date).toLocaleDateString('en-GB') : '-';
            const tedToStr = tedTo.date ? new Date(tedTo.date).toLocaleDateString('en-GB') : '-';
            generatedDisplayValue = `Date from ${tedFromStr} - Date until ${tedToStr}`;
          } else {
            generatedDisplayValue = `day from ${tedFrom.days || 0} months from ${tedFrom.months || 0} - day until ${tedTo.days || 0} months until ${tedTo.months || 0}`;
          }
          break;

        case 'PRODUCT_CATEGORY':
          const pcFrom = backendFilter.from || {};
          const pcTo = backendFilter.to || {};
          if (pcFrom.date || pcTo.date) {
            const pcFromStr = pcFrom.date ? new Date(pcFrom.date).toLocaleDateString('en-GB') : '-';
            const pcToStr = pcTo.date ? new Date(pcTo.date).toLocaleDateString('en-GB') : '-';
            generatedDisplayValue = `Level : ${backendFilter.productCategoryLevel || ''} Transaction date from ${pcFromStr} - Transaction date until ${pcToStr}`;
          } else {
            generatedDisplayValue = `Level : ${backendFilter.productCategoryLevel || ''} day from ${pcFrom.days || 0} months from ${pcFrom.months || 0} - day until ${pcTo.days || 0} months until ${pcTo.months || 0}`;
          }
          break;

        case 'MEMBER_NAMED_FLAG':
        case 'PERSON_NAMED_FLAG':
          if (backendFilter.flagChannel && backendFilter.namedFlag) {
            const channelFlags = state.optOutFlags[backendFilter.flagChannel as keyof typeof state.optOutFlags] || [];
            const matchedFlag = channelFlags.find((f: any) => f.name === backendFilter.namedFlag);
            generatedDisplayValue = matchedFlag
              ? `${backendFilter.flagChannel} - ${matchedFlag.displayName}`
              : `${backendFilter.flagChannel} - ${backendFilter.namedFlag}`;
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Choose a consent';
          }
          break;

        case 'MEMBER_POINTS':
          const opLabel = (backendFilter.comperator || '').replace(/_/g, ' ').toLowerCase();
          generatedDisplayValue = `${opLabel} ${backendFilter.memberPoints ?? 0} points`;
          break;

        case 'MEMBER_CHILDREN_COUNT':
        case 'PERSON_CHILDREN_COUNT':
          const ccOpLabel = (backendFilter.comperator || '').replace(/_/g, ' ').toLowerCase();
          generatedDisplayValue = `${ccOpLabel} ${backendFilter.childrenCount || 0} child/children`;
          break;

        case 'MEMBER_DATA_ENTRY_DATE':
          if (!backendFilter.durationType) {
            backendFilter.durationType = backendFilter.dataEntryDate ? 'DATE' : 'DAYS';
          }
          const dedOpLabel = (backendFilter.comparator || '').replace(/_/g, ' ').toLowerCase();
          if (backendFilter.dataEntryDate) {
            generatedDisplayValue = `${dedOpLabel} ${new Date(backendFilter.dataEntryDate).toLocaleDateString('en-GB')}`;
          } else {
            generatedDisplayValue = `${dedOpLabel} ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
          }
          break;

        case 'MEMBER_CSI_SCORE':
        case 'PERSON_CSI_SCORE':
          generatedDisplayValue = `Score between ${backendFilter.min || 0} and ${backendFilter.max || 0}`;
          break;

        case 'MEMBER_CAMPAIGN_EMAIL_REPORT_CLICKED':
        case 'PERSON_CAMPAIGN_EMAIL_REPORT_CLICKED':
        case 'MEMBER_CAMPAIGN_EMAIL_REPORT_OPENED':
        case 'PERSON_CAMPAIGN_EMAIL_REPORT_OPENED':
        case 'MEMBER_CAMPAIGN_INAPP_REPORT_CLICKED':
        case 'PERSON_CAMPAIGN_INAPP_REPORT_CLICKED':
        case 'MEMBER_CAMPAIGN_INAPP_REPORT_OPENED':
        case 'PERSON_CAMPAIGN_INAPP_REPORT_OPENED':
        case 'MEMBER_CAMPAIGN_PUSH_REPORT_CLICKED':
        case 'PERSON_CAMPAIGN_PUSH_REPORT_CLICKED':
          const cerOpLabel = (backendFilter.comparator || '').replace(/_/g, ' ').toLowerCase();
          if (backendFilter.executionDate) {
            generatedDisplayValue = `${cerOpLabel} ${new Date(backendFilter.executionDate).toLocaleDateString('en-GB')}`;
          } else if (backendFilter.days || backendFilter.months) {
            generatedDisplayValue = `${cerOpLabel} ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Configure campaign reporting';
          }
          break;

        case 'MEMBER_MTV_ANALYSIS':
          const mtvIds: number[] = backendFilter.tableMTVIds || [];
          if (mtvIds.length > 0) {
            generatedDisplayValue = `${mtvIds.length} segment(s) selected`;
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select MTV segments';
          }
          break;

        case 'MEMBER_REGISTRATION_DATE':
        case 'PERSON_REGISTRATION_DATE':
          backendFilter.durationType = backendFilter.registrationDate ? 'date' : 'daysMonths';
          const regOpLabel = (backendFilter.comperator || '').replace(/_/g, ' ').toLowerCase();
          if (backendFilter.registrationDate) {
            const dStr = new Date(backendFilter.registrationDate).toLocaleDateString('en-GB').replace(/\//g, '/');
            generatedDisplayValue = `${regOpLabel} ${dStr}`;
          } else {
            generatedDisplayValue = `${regOpLabel} ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
          }
          break;

        case 'MEMBER_POINTLOG_EXPIRATION':
          backendFilter.durationType =
            backendFilter.fromExpirationDate || backendFilter.untilExpirationDate ? 'date' : 'daysMonths';
          if (backendFilter.durationType === 'date') {
            const fromStr = backendFilter.fromExpirationDate
              ? new Date(backendFilter.fromExpirationDate).toLocaleDateString('en-GB')
              : '';
            const untilStr = backendFilter.untilExpirationDate
              ? new Date(backendFilter.untilExpirationDate).toLocaleDateString('en-GB')
              : '';
            generatedDisplayValue = `Pointlog expiration date from ${fromStr} - Pointlog expiration date until ${untilStr}`;
          } else {
            generatedDisplayValue = `Pointlog expiration day from ${backendFilter.fromDays || 0} Pointlog expiration months from ${backendFilter.fromMonths || 0} - Pointlog expiration day until ${backendFilter.untilDays || 0} Pointlog expiration months until ${backendFilter.untilMonths || 0}`;
          }
          break;
        case 'MEMBER_SOCIAL_MEDIA_PAGE_LIKES':
          if (backendFilter.durationType === 'DAYS') {
            generatedDisplayValue = `Days: ${backendFilter.days || 0}, Months: ${backendFilter.months || 0}`;
          } else if (backendFilter.durationType === 'DATE' && backendFilter.dateFrom) {
            generatedDisplayValue = `Date From: ${backendFilter.dateFrom.split('T')}`;
          } else {
            generatedDisplayValue = templateData.defaults['headerPlaceholder'] || 'Select account and timeframe';
          }
          break;

        case 'MEMBER_GENERIC_ACTIVITY':
          const gaSubtype = backendFilter.subType || '';
          if (backendFilter.dateFrom || backendFilter.dateUntil) {
            const gaFrom = backendFilter.dateFrom ? new Date(backendFilter.dateFrom).toLocaleDateString('en-GB') : '-';
            const gaUntil = backendFilter.dateUntil
              ? new Date(backendFilter.dateUntil).toLocaleDateString('en-GB')
              : '-';
            generatedDisplayValue = `${gaSubtype} from ${gaFrom} to ${gaUntil}`;
          } else if (backendFilter.days || backendFilter.months) {
            generatedDisplayValue = `${gaSubtype} in: ${backendFilter.days || 0} days and ${backendFilter.months || 0} months`;
          } else {
            generatedDisplayValue = gaSubtype || templateData.defaults['headerPlaceholder'] || 'Configure activity';
          }
          break;
      }

      return {
        label: templateData.label,
        category: templateData.category,
        ...templateData.defaults,
        ...backendFilter,
        displayValue: generatedDisplayValue,
        instanceId: Date.now() + index,
      };
    });

    return {
      ...state,
      ...campaignScopedReset,
      selectedFilters: enrichedFilters,
      controlGroupPercentage: campaign.data.campaignExecutionConfig?.controlGroupPercentage || 0,
      recipientLimit: campaign.data.recipientLimit,
      selectedCampaignAdhocList: matchedList ? matchedList : listId ? ({ campaignAdHocListId: listId } as any) : null,
      selectedCampaignAdhocListName: matchedList ? matchedList.listName : '',
      additionalRecipientsPanelExpanded: !!listId,
    };
  }),
  on(HelpTextChanged, (state) => ({
    ...state,
    helpTextStatus: !state.helpTextStatus,
  })),
  on(AdvancedHelpTextChanged, (state) => ({
    ...state,
    advancedHelpTextStatus: !state.advancedHelpTextStatus,
  })),
  on(TargetGroupFiltersPanelChanged, (state) => ({
    ...state,
    targetGroupFiltersPanelExpanded: !state.targetGroupFiltersPanelExpanded,
  })),
  on(AdvancedFilterConfigurationPanelChanged, (state) => ({
    ...state,
    advancedFilterConfigurationPanelExpanded: !state.advancedFilterConfigurationPanelExpanded,
  })),
  on(AdditionalRecipientsPanelChanged, (state) => ({
    ...state,
    additionalRecipientsPanelExpanded: !state.additionalRecipientsPanelExpanded,
  })),
  on(SelectionsByTargetSuccess, (state, { selections }) => ({
    ...state,
    selections,
  })),
  on(MemberCategoriesSuccess, (state, { categories }) => ({
    ...state,
    memberCategories: categories,
  })),
  on(CardTypesSuccess, (state, { cardTypes }) => ({
    ...state,
    cardTypes,
  })),
  on(SiriusAccountsSuccess, (state, { siriusAccounts }) => ({
    ...state,
    siriusAccounts,
  })),
  on(SocialMediaProvidersSuccess, (state, { socialMediaProviders }) => ({
    ...state,
    socialMediaProviders,
  })),
  on(StoresSuccess, (state, { stores }) => ({
    ...state,
    stores,
  })),
  on(CampaignAdhocListsSuccess, (state, { campaignAdhocLists }) => {
    let currentSelectedList = state.selectedCampaignAdhocList;
    let currentSelectedName = state.selectedCampaignAdhocListName;
    if (currentSelectedList && currentSelectedList.campaignAdHocListId && !currentSelectedName) {
      const match = campaignAdhocLists.find((l) => l.campaignAdHocListId === currentSelectedList?.campaignAdHocListId);

      if (match) {
        currentSelectedList = match;
        currentSelectedName = match.listName;
      }
    }

    return {
      ...state,
      campaignAdhocLists,
      selectedCampaignAdhocList: currentSelectedList,
      selectedCampaignAdhocListName: currentSelectedName,
    };
  }),
  on(CampaignAdhocListChanged, (state, { adhocList }) => ({
    ...state,
    selectedCampaignAdhocList: adhocList,
  })),
  on(CampaignAdhocListNameChanged, (state, { adhocListName }) => ({
    ...state,
    selectedCampaignAdhocListName: adhocListName,
  })),
  on(CampaignAdhocListNameCleared, (state) => ({
    ...state,
    selectedCampaignAdhocList: null,
    selectedCampaignAdhocListName: '',
  })),
  on(ControlGroupPercentageChanged, (state, { controlGroupPercentage }) => ({
    ...state,
    controlGroupPercentage,
  })),
  on(RecipientLimitChanged, (state, { recipientLimit }) => ({
    ...state,
    recipientLimit,
  })),
  on(FilterSelected, (state, { selectedFilter }) => ({
    ...state,
    selectedFilters: [...state.selectedFilters, selectedFilter],
  })),
  on(RemoveFilter, (state, { index }) => ({
    ...state,
    selectedFilters: state.selectedFilters.filter((_, i) => i !== index),
  })),
  on(FilterUpdated, (state, { index, changes }) => {
    const updatedFilters = state.selectedFilters.map((item, i) => {
      if (i === index) {
        return { ...item, ...changes };
      }
      return item;
    });

    return {
      ...state,
      selectedFilters: updatedFilters,
    };
  }),
  on(ShowFilterInfo, (state) => ({
    ...state,
    showFilterInfo: !state.showFilterInfo,
  })),
  on(OpenFilterConfirmationModal, (state, { source }) => ({
    ...state,
    filterConfirmationModalSource: source,
  })),
  on(EstimationRunInit, (state) => ({
    ...state,
    estimationPolling: true,
  })),
  on(EstimationRunSuccess, (state, { campaignEstimationIdentifier }) => ({
    ...state,
    campaignEstimationIdentifier,
  })),
  on(EstimationRunFailure, (state) => ({
    ...state,
    estimationPolling: false,
  })),
  on(EstimationResultSuccess, (state, { estimationResult }) => ({
    ...state,
    estimationResult,
    estimationPolling: false,
  })),
  on(EstimationResultFailure, (state) => ({
    ...state,
    estimationPolling: false,
  })),
  on(ClearEstimationResult, (state) => ({
    ...state,
    estimationResult: null,
  })),
  on(CancelEstimationPolling, (state) => ({
    ...state,
    estimationPolling: false,
  })),
  on(ResetWizard, (state) => ({
    ...initialState,
    ...preserveLookups(state),
  })),
  on(CampaignSelectionTargetChanged, () => ({
    ...initialState,
  })),
  on(SearchCampaignsSuccess, (state, { res }) => ({
    ...state,
    searchedCampaigns: res,
  })),
  on(SearchCampaignsFailure, (state) => ({
    ...state,
    searchedCampaigns: [],
  })),
  on(GetOptOutFlagsSuccess, (state, { optOutFlags }) => ({
    ...state,
    optOutFlags,
  })),
  on(GetMemberStatusesSuccess, (state, { memberStatuses }) => ({
    ...state,
    memberStatuses,
  })),
  on(GetCouponSetsSuccess, (state, { couponSets }) => ({
    ...state,
    couponSets,
  })),
  on(GetCouponSetAttributesSuccess, (state, { attributes }) => ({
    ...state,
    attributeCodes: attributes,
  })),
  on(getPointSourcesSuccess, (state, { pointSources }) => ({
    ...state,
    pointSources,
  })),
  on(getMemberSourcesSuccess, (state, { memberSources }) => ({
    ...state,
    memberSources,
  })),
  on(getAvailableLanguagesSuccess, (state, { availableLanguages }) => ({
    ...state,
    availableLanguages,
  })),
  on(getMemberClubsSuccess, (state, { memberClubs }) => ({
    ...state,
    memberClubs,
  })),
  on(getTagsSuccess, (state, { tags }) => ({
    ...state,
    tags,
  })),
  on(getCommunicationChannelsSuccess, (state, { communicationChannels }) => ({
    ...state,
    communicationChannels,
  })),
  on(GetCouponSetsFastSuccess, (state, { couponSets }) => ({
    ...state,
    couponSets,
  })),
  on(getTiersSuccess, (state, { tiers }) => ({
    ...state,
    tiers,
  })),
  on(getRFMSuccess, (state, { rfm }) => ({
    ...state,
    rfm,
  })),
  on(getCountriesSuccess, (state, { countries }) => ({
    ...state,
    countries,
  })),
  on(getRegionsSuccess, (state, { regions }) => ({
    ...state,
    regions,
  })),
  on(getMTVSuccess, (state, { mtv }) => ({
    ...state,
    mtv,
  })),
  on(getPersonSourcesSuccess, (state, { personSources }) => ({
    ...state,
    personSources,
  })),
  on(getReportedEmailCampaignsSuccess, (state, { reportedEmailCampaigns }) => ({
    ...state,
    reportedEmailCampaigns,
  })),
  on(getReportedInAppCampaignsSuccess, (state, { reportedInAppCampaigns }) => ({
    ...state,
    reportedInAppCampaigns,
  })),
  on(getReportedPushCampaignsSuccess, (state, { reportedPushCampaigns }) => ({
    ...state,
    reportedPushCampaigns,
  })),
  on(getProductCategoryLevelsSuccess, (state, { levels }) => ({
    ...state,
    productCategoryLevels: levels,
  })),
  on(getProductCategoriesSuccess, (state, { categories }) => ({
    ...state,
    productCategories: categories,
  })),
  on(getGenericActivityTypesSuccess, (state, { data }) => ({
    ...state,
    genericActivityTypes: data,
  })),
);
